import React, { useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import Heading from '@legacy-vfuk/core-heading';
import { Container } from '@legacy-vfuk/core-container';
import { SimpleGrid } from '@legacy-vfuk/core-simple-grid';
import { Icon } from '@legacy-vfuk/core-icon';
import { Pill } from '@legacy-vfuk/core-pill';
import { Link } from 'gatsby';
import {
  ArticleClickEventData,
  ArticleProps,
  CustomWindow,
} from './types/ArticleCard.types';
import replaceSlug from '../../../../helpers/replaceSlugs';
import reduceLookUpTable from '../../../../helpers/reduceLookUpTable';
import {
  ArticleWrapper,
  InfoWrapper,
  IconWithTextWrapper,
  ImageWrapper,
  TagPillWrapper,
  SaveIconWrapper,
  ContentTypeIconWrapper,
  ContentLevelWrapper,
  ImageOverlay,
  MediaContentTypeIconWrapper,
  MediaImageWrapper,
} from './styles/ArticleCard.styles';
import { DirectionContext } from '../../../../contexts/DirectionContext';
import SaveArticleIcon from '../../../SaveArticleIcon/SaveArticleIcon';
import ArticleCardThumbnail from './ArticleCardThumbnail';
import Paragraph from '@legacy-vfuk/core-paragraph';
import IconButton from '@legacy-vfuk/core-icon-button';
import { ArticleTag } from '../../Sections/SectionFilterableArticles/types/SectionFilterableArticles.types';

const queryLookupData = graphql`
  {
    contentfulSlugCharacterLookup {
      name
      exceptions
      lookup {
        lookup
      }
    }
  }
`;

declare const window: CustomWindow;

const fireArticleClickEvent = (trackingData: ArticleClickEventData) => {
  if (window.onArticleClickEvent) {
    try {
      // fire the utag link event
      window.onArticleClickEvent(trackingData);
    } catch (e) {
      console.log(
        'onArticleClickEvent failed - please check the Article Clicks SEO script'
      );
    }
  }
};

const sortByMatch: (a: string[], b: string[]) => string[] = (
  categories,
  queryCategories
) => {
  const result = categories.slice();

  result.sort((a, b) => {
    const matchA = queryCategories.includes(a);
    const matchB = queryCategories.includes(b);

    if (matchA && !matchB) {
      return -1;
    } else if (!matchA && matchB) {
      return 1;
    } else {
      return 0;
    }
  });

  return result.slice(0, 2);
};

export const ArticleCard: React.FC<ArticleProps> = ({
  data,
  indexSlug,
  seoTitle,
  appliedFilters,
  queryCategories = [],
  viewCount,
  readingTimeMinuteLabel,
  displaySaveIcon,
  saveLabelText,
  handleSaveClick,
  isAnimated,
  isFeatured,
  isMedia,
  label,
  withTagLabel = true,
}: ArticleProps) => {
  const {
    pageArticleName: name,
    mainImage,
    articleThumbnailImage,
    summary,
    slug,
    publishingDate,
    categoryTags,
    topicTags,
    contentType,
    readingTime,
    contentLevel,
  } = data;

  // Replace characters within the slug, using the Slug character look up from Contentful
  const { contentfulSlugCharacterLookup } = useStaticQuery(queryLookupData);

  const direction = useContext(DirectionContext);

  const exceptions = contentfulSlugCharacterLookup?.exceptions;
  const lookup = contentfulSlugCharacterLookup?.lookup?.lookup;

  const slugCharLookUpTable = reduceLookUpTable(lookup);
  const replacedSlug = replaceSlug(slug, slugCharLookUpTable, exceptions);

  const tags: ArticleTag[] = topicTags.length ? topicTags : categoryTags;
  const tagsName = tags.map(tag => tag.displayName || tag.name);
  const sortedTagNames = sortByMatch(tagsName, queryCategories);

  const splitReplacedSlug = replacedSlug.split('/');
  const updatedSlug =
    splitReplacedSlug.length === 2
      ? splitReplacedSlug[1]
      : splitReplacedSlug[0];

  if (isFeatured) {
    return (
      <ArticleWrapper direction={direction} animated={isAnimated}>
        <Link
          to={`/${indexSlug}/${updatedSlug}`}
          title={name}
          data-testid={`article-${updatedSlug}`}
          data-analytics-element={name}
          data-analytics-category="article"
          data-analytics-context={seoTitle}
          data-view-count={viewCount}
          onClick={() =>
            fireArticleClickEvent({ appliedFilters, contentName: name })
          }
        >
          <ImageWrapper isFeatured>
            <TagPillWrapper>
              {label ? (
                <Pill text={label} appearance="primary" />
              ) : (
                withTagLabel &&
                sortedTagNames.map(tag => (
                  <Pill text={tag} appearance={'alt2'} key={tag} />
                ))
              )}
            </TagPillWrapper>

            <SaveIconWrapper>
              <SaveArticleIcon
                show={displaySaveIcon}
                onArticle={false}
                saveLabel={saveLabelText}
                useSmallerText={
                  readingTimeMinuteLabel?.length > 3 ? true : false
                }
                handleSaveClick={() => handleSaveClick(slug)}
              />
            </SaveIconWrapper>

            {contentType && (
              <ContentTypeIconWrapper>
                <SimpleGrid spacing={2} layout={{ sm: [12] }}>
                  <IconButton
                    icon={{ name: contentType.iconName }}
                    shape="circle"
                    srText={contentType.displayName}
                    size={2}
                    inverse
                    appearance="primary"
                  />
                  <Heading noMargin size={2} weight={2} inverse>
                    {name}
                  </Heading>
                  <Paragraph size={2} weight={2} inverse>
                    <span className="summary">{summary}</span>
                  </Paragraph>
                </SimpleGrid>
              </ContentTypeIconWrapper>
            )}

            <ArticleCardThumbnail image={articleThumbnailImage || mainImage} />
            <ImageOverlay />
          </ImageWrapper>
        </Link>
      </ArticleWrapper>
    );
  }

  if (isMedia) {
    return (
      <ArticleWrapper
        direction={direction}
        animated={isAnimated}
        media
        elevated
      >
        <Link
          to={`/${indexSlug}/${updatedSlug}`}
          title={name}
          data-testid={`article-${updatedSlug}`}
          data-analytics-element={name}
          data-analytics-category="article"
          data-analytics-context={seoTitle}
          data-view-count={viewCount}
          onClick={() =>
            fireArticleClickEvent({ appliedFilters, contentName: name })
          }
        >
          <div>
            <MediaImageWrapper>
              <TagPillWrapper>
                {sortedTagNames.map(tag => (
                  <Pill text={tag} appearance={'alt2'} key={tag} />
                ))}
              </TagPillWrapper>

              <SaveIconWrapper>
                <SaveArticleIcon
                  show={displaySaveIcon}
                  onArticle={false}
                  saveLabel={saveLabelText}
                  useSmallerText={
                    readingTimeMinuteLabel?.length > 3 ? true : false
                  }
                  handleSaveClick={() => handleSaveClick(slug)}
                />
              </SaveIconWrapper>

              {contentType && (
                <MediaContentTypeIconWrapper>
                  <IconButton
                    icon={{ name: contentType.iconName }}
                    shape="circle"
                    srText={contentType.displayName}
                    size={2}
                    inverse
                    appearance="primary"
                  />
                </MediaContentTypeIconWrapper>
              )}

              <ArticleCardThumbnail
                image={articleThumbnailImage || mainImage}
              />
            </MediaImageWrapper>
            <Container>
              <SimpleGrid spacing={3} layout={{ sm: [12, 12] }}>
                <Heading noMargin size={1} weight={3}>
                  {name}
                </Heading>

                <Paragraph size={2} weight={2}>
                  <span className="summary">{summary}</span>
                </Paragraph>
              </SimpleGrid>
            </Container>
          </div>
        </Link>
      </ArticleWrapper>
    );
  }

  return (
    <ArticleWrapper direction={direction} animated={isAnimated} elevated>
      <Link
        to={`/${indexSlug}/${updatedSlug}`}
        title={name}
        data-testid={`article-${updatedSlug}`}
        data-analytics-element={name}
        data-analytics-category="article"
        data-analytics-context={seoTitle}
        data-view-count={viewCount}
        onClick={() =>
          fireArticleClickEvent({ appliedFilters, contentName: name })
        }
      >
        <div>
          <ImageWrapper>
            <TagPillWrapper>
              {label ? (
                <Pill text={label} appearance="primary" />
              ) : (
                sortedTagNames.map(tag => (
                  <Pill text={tag} appearance={'alt2'} key={tag} />
                ))
              )}
            </TagPillWrapper>

            <SaveIconWrapper>
              <SaveArticleIcon
                show={displaySaveIcon}
                onArticle={false}
                saveLabel={saveLabelText}
                useSmallerText={
                  readingTimeMinuteLabel?.length > 3 ? true : false
                }
                handleSaveClick={() => handleSaveClick(slug)}
              />
            </SaveIconWrapper>

            {contentType && (
              <ContentTypeIconWrapper>
                <IconButton
                  icon={{ name: contentType.iconName }}
                  shape="circle"
                  srText={contentType.displayName}
                  size={2}
                  inverse
                  appearance="primary"
                />
              </ContentTypeIconWrapper>
            )}

            <ArticleCardThumbnail image={articleThumbnailImage || mainImage} />
          </ImageWrapper>
          <Container>
            <SimpleGrid spacing={3} layout={{ sm: [12, 12] }}>
              {contentLevel && (
                <ContentLevelWrapper>
                  <Paragraph size={1} weight={2} noMargin>
                    {contentLevel.displayName}
                  </Paragraph>
                </ContentLevelWrapper>
              )}
              <InfoWrapper>
                <IconWithTextWrapper
                  useSmallerText={
                    readingTimeMinuteLabel?.length > 3 ? true : false
                  }
                >
                  <Icon name="calendar-item" size={3} />
                  <p style={{ direction: 'ltr' }}>{publishingDate}</p>
                </IconWithTextWrapper>
                {readingTime > 0 && (
                  <IconWithTextWrapper
                    useSmallerText={
                      readingTimeMinuteLabel?.length > 3 ? true : false
                    }
                  >
                    <Icon name="clock" size={3} />
                    <p>
                      {readingTime} {readingTimeMinuteLabel}
                    </p>
                  </IconWithTextWrapper>
                )}
              </InfoWrapper>
              <Heading noMargin size={2} weight={2}>
                {name}
              </Heading>

              <Paragraph size={2} weight={2}>
                <span className="summary">{summary}</span>
              </Paragraph>
            </SimpleGrid>
          </Container>
        </div>
      </Link>
    </ArticleWrapper>
  );
};
